import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { OptionType, useModal } from '@europrocurement/flexy-components';
import {
    DossierRoutingContext,
    PROCESS_STATUS_FLAG_ONGOING,
    REFRESH_CURRENT_DOSSIER_CTX,
    RESET_DOS_DOCS_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { useSnackbar } from 'notistack';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { useNavigate } from 'react-router';
import DossierFormaliteProcessListRows from './DossierFormaliteProcessListRows';
import DossierFormaliteProcessListHeaderCells from './DossierFormaliteProcessListHeaderCells';
import DissociateFormalitiesForm from '../DissociateFormalitiesForm/DissociateFormalitiesForm';
import MoveFormalitiesForm from '../MoveFormalitiesToOtherDossierForm/MoveFormalitiesToOtherDossierForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import NavigateToExternalUrlModal from '../NavigateToExternalUrlModal/NavigateToExternalUrlModal';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';

export type DossierFormaliteProcessProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
};

export type DossierFormaliteProcessesListProps = {
    formalities: DossierFormaliteProcessProps[];
    publishable: boolean;
};

export type SelectedFormality = {
    id: number;
};

const DossierFormaliteProcessesList: React.FunctionComponent<DossierFormaliteProcessesListProps> =
    function (props) {
        const { formalities, publishable } = props;
        const { modalActions } = useModal();
        const { getDossierDocumentsWithFiles } = useDossierDocumentsCtx();
        const { dossier, siblings } = useCurrentDossierCtx();
        const dispatch = useCurrentDossierCtxDispatch();
        const dossierDocumentsdispatch = useDossierDocumentsCtxDispatch();
        const navigate = useNavigate();
        const { getDossierRoute } = React.useContext(DossierRoutingContext);

        const refreshDossier = useCallback(
            (dossierId?: number) => {
                if (dispatch) {
                    dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
                }
                if (dossierDocumentsdispatch) {
                    dossierDocumentsdispatch({ type: RESET_DOS_DOCS_CTX });
                }
                if (dossierId) {
                    const route = getDossierRoute(dossierId as number);
                    if (route) {
                        navigate(route);
                    }
                }
            },
            [dispatch, dossierDocumentsdispatch, getDossierRoute, navigate],
        );

        const { enqueueSnackbar } = useSnackbar();
        const {
            hasOnGoingProcesses,
            getCurrentProcessOfType,
            canChangeProcessStatus,
            toggleGreffeValidator,
            canToggleGreffeValidator,
            getNotPublishableReason,
            deleteFormality,
            getFormalitiesByProcess,
            canDeleteFormality,
        } = useDossierProcesses();
        const { actionUpdateValidatorStatus, actionDeleteFormality } =
            useDossierProcessesActionsAndRender();

        const process = useMemo(
            () => (dossier ? getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE) : undefined),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [dossier],
        );

        const { getDossierValidatorById } = useDossierValidatorsCtx();
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];
        const actions = [];

        const filteredSiblings =
            (siblings &&
                dossier &&
                siblings.filter((doss) => doss.id !== dossier.id && doss.deleted === false)) ||
            [];

        const [selectedFormalitiesIds, setSelectedFormalitiesIds] = useState<number[]>([]);

        const handleOnUpdateStatus = () => {
            if (!process || !canChangeProcessStatus(process)) {
                return;
            }

            modalActions.call(
                <UpdateValidatorForm
                    processing={false}
                    process={process}
                    onItemSelected={(newProcess) => {
                        refreshDossier();

                        if (newProcess && newProcess.externalUrl) {
                            modalActions.call(
                                <NavigateToExternalUrlModal
                                    externalUrl={newProcess.externalUrl as string}
                                />,
                            );
                        } else {
                            modalActions.close();
                        }
                    }}
                    statuses={statuses.filter(
                        (status) =>
                            status.statusFlag !== PROCESS_STATUS_FLAG_ONGOING || publishable,
                    )}
                />,
            );
        };

        const handleDissociate = () => {
            if (!dossier) {
                return;
            }
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, dissociation impossible',
                    { variant: 'error' },
                );
                return;
            }

            modalActions.call(
                <DissociateFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocumentsWithFiles()}
                    onSubmitDone={refreshDossier}
                />,
            );
        };

        const handleMove = () => {
            if (!dossier) {
                return;
            }
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, déplacement impossible',
                    { variant: 'error' },
                );
                return;
            }
            const options = filteredSiblings.map((doss) => {
                const nbrFormalities = doss.formalites.length;

                return {
                    value: doss.id,
                    label: `${doss.numero} - ${doss.societeDenomination} - ${
                        nbrFormalities > 1
                            ? `${doss.formalites[0].libelle} + ${nbrFormalities - 1} `
                            : doss.formalites[0].libelle
                    }`,
                } as OptionType;
            });

            modalActions.call(
                <MoveFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocumentsWithFiles()}
                    onSubmitDone={refreshDossier}
                    options={options}
                />,
            );
        };

        const handleToggleValidator = async () => {
            if (!process) {
                return;
            }

            try {
                await toggleGreffeValidator(dossier as DossierOpenSearch, process);
                refreshDossier();
            } catch (e) {
                enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
                console.error(e);
            }
        };

        const allowChangeProcessStatus = process && canChangeProcessStatus(process);

        if (allowChangeProcessStatus) {
            actions.push(actionUpdateValidatorStatus(handleOnUpdateStatus));
        }

        if (
            process &&
            dossier &&
            process.validatorTypeId === VALIDATOR_TYPE_GREFFE &&
            canToggleGreffeValidator(dossier)
        ) {
            actions.push({
                label:
                    process.validatorId === VALIDATOR_DEMAT_FACILE
                        ? 'Passer en Guichet Unique'
                        : 'Passer en Demat Facile',
                icon: 'rotate-reverse' as IconName,
                onClick: handleToggleValidator,
            });
        }

        if (process && dossier) {
            const processFormalities = getFormalitiesByProcess(dossier, [process]);

            if (
                processFormalities.length &&
                processFormalities[0] !== undefined &&
                canDeleteFormality(dossier)
            ) {
                actions.push(
                    actionDeleteFormality(
                        async () => {
                            modalActions.close();

                            try {
                                await deleteFormality(processFormalities[0], dossier);
                                enqueueSnackbar('La démarche a bien été supprimée');
                                refreshDossier();
                            } catch (e) {
                                console.error(e);
                                enqueueSnackbar(
                                    'Une erreur est survenue lors de la suppression de la démarche',
                                    {
                                        variant: 'error',
                                    },
                                );
                            }
                        },
                        () => modalActions.close(),
                    ),
                );
            }
        }

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <DossierFormaliteProcessListHeaderCells
                        isMulipleFormalities={formalities.length > 1}
                        showStatus={process !== undefined}
                    />
                </Box>

                <DossierProcessesBulkActionsBar
                    selectedIds={selectedFormalitiesIds}
                    onClearSelection={() => setSelectedFormalitiesIds([])}
                    canDissociate={formalities.length > 1}
                    onDissociate={handleDissociate}
                    canMove={filteredSiblings.length > 0}
                    onMove={handleMove}
                />

                <Box>
                    <DossierFormaliteProcessListRows
                        formalities={formalities}
                        selectedFormalitiesIds={selectedFormalitiesIds}
                        setSelectedFormalitiesIds={setSelectedFormalitiesIds}
                        actions={actions}
                        process={
                            process
                                ? {
                                      validatorId: process.validatorId as number,
                                      validatorLabel: process.validatorLabel as string,
                                      statusLabel: process.statusLabel,
                                      statusFlag: process.statusFlag,
                                      onStatusClick: allowChangeProcessStatus
                                          ? handleOnUpdateStatus
                                          : undefined,
                                  }
                                : undefined
                        }
                        warningReason={getNotPublishableReason(dossier as DossierOpenSearch)}
                    />
                </Box>
            </Box>
        );
    };

export default DossierFormaliteProcessesList;
